import { IdentityConfigData } from 'types/config'
import IdentityConfig, { type IdentityServerResourceUrl } from './identityConfig.model'
import { checkParamsToOverrideLocalConfig, getCustomResourceBaseUrlFromConfigLocation } from './identityConfig.normalizer'
import { configOverwriteSchema } from '../../consts'

interface IdentityServer extends IdentityServerResourceUrl {
  fetchBrandConfigFile(
    key?: string,
    language?: string,
    preview?: boolean,
    customConfigLocationBaseUrl?: string | null
  ): Promise<Record<string, any>>
}

export async function request({
  server,
  key,
  configLocationHost,
  language,
  preview,
  storedConfig = null,
  identityConfigOverrides = null,
}: {
  server: IdentityServer
  key?: string
  configLocationHost?: string
  language?: string
  preview?: boolean
  storedConfig?: Record<string, any> | null
  identityConfigOverrides?: Record<string, any> | null
}): Promise<IdentityConfig> {
  const customConfigLocationBaseUrl = (configLocationHost === 'null' || !configLocationHost)
    ? null
    : configLocationHost.replace(/\/?$/, '/')

  try {
    let brandConfig = await server.fetchBrandConfigFile(
      key,
      language,
      preview,
      customConfigLocationBaseUrl,
    )

    if (identityConfigOverrides) {
      brandConfig = checkParamsToOverrideLocalConfig(
        brandConfig,
        identityConfigOverrides,
        configOverwriteSchema as unknown as Record<string, any>,
      )
    }

    const mergedConfig: IdentityConfigData & { language?: string } = {
      ...(brandConfig as IdentityConfigData),
      ...(storedConfig || {}),
      language,
    }

    const customResourceBaseUrl = getCustomResourceBaseUrlFromConfigLocation(
      customConfigLocationBaseUrl,
    )
    return new IdentityConfig(mergedConfig, server, customResourceBaseUrl, key)
  } catch (error) {
    if (configLocationHost) {
      window.console.error(
        'configLocationHost property error, using default host to fetch the config file:',
        error,
      )
      return request({
        server,
        key,
        language,
        preview,
        storedConfig,
        identityConfigOverrides,
      })
    }
    throw error
  }
}

export function getConfigurationKey(): string {
  const nbcAuthenticateUrlRegex = /id\.nbc\.com\/websdk\/v[\d.\w.-]+\/authenticate/
  const sportsEdgeAuthenticateUrlRegex = /id\.nbcsportsedge\.com\/websdk\/v[\d.\w.-]+\/authenticate/
  const eOnlineAuthenticateUrlRegex = /id\.eonline\.com\/websdk\/v[\d.\w.-]+\/authenticate/
  if (window.location.origin.includes('https://localhost.nbcuni.com:4000')) return 'nbc'
  if (nbcAuthenticateUrlRegex.test(window.location.href)) return 'nbc'
  if (sportsEdgeAuthenticateUrlRegex.test(window.location.href)) return 'sportsedge'
  if (eOnlineAuthenticateUrlRegex.test(window.location.href)) return 'eolweb'
  return 'nbc'
}
